import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { copy } from '../copy/es';
import { api, ErrorApi, textoDeError } from '../lib/api';
import { VencidoPage } from './VencidoPage';
import { capitalizar, nombrePeriodo } from '../lib/formato';
import { TIPOS_DOCUMENTO } from '@reembolsables/shared';

interface Resumen {
  correo: string;
  periodoVigente: string;
  venceEn: string;
  periodos: string[];
  centros: {
    id: string;
    codigo: string;
    proyecto: string;
    tercero: string;
    periodos: {
      periodo: string;
      total: number;
      tipos: { tipo: string; nombre: string; cantidad: number }[];
    }[];
  }[];
}
interface Documentos {
  tipos: {
    tipo: string;
    nombre: string;
    cantidad: number;
    documentos: { id: string; nombreArchivo: string; tamanoBytes: number }[];
  }[];
}

const tamano = (b: number) =>
  b > 1_048_576 ? `${(b / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
/** Icon colour class per group, as in mockup screen 5; abbreviation and detail come from the shared catalogue. */
const COLOR_ICONO: Record<string, string> = {
  facturas: 'i4',
  contratos: 'i6',
  actas: 'i1',
  'causacion-contable': 'i5',
  nomina: 'i2',
  anexos: 'i3',
  otros: 'i7',
};

/** Mockup screen 5: period download on top, cost-center accordion with groups, history links. Never an amount (V). */
export function PortalPage() {
  const { token = '' } = useParams();
  const base = `/api/p/${token}`;
  const c = copy.portal;
  const [resumen, setResumen] = useState<Resumen | null>(null);
  const [vencido, setVencido] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [periodo, setPeriodo] = useState<string>('');
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const [docs, setDocs] = useState<Record<string, Documentos>>({});
  const [listas, setListas] = useState<Set<string>>(new Set());

  useEffect(() => {
    api<Resumen>(base)
      .then((r) => {
        setResumen(r);
        setPeriodo(r.periodoVigente);
        if (r.centros[0]) setAbiertos(new Set([r.centros[0].id]));
      })
      .catch((e) => {
        if (e instanceof ErrorApi && (e.estado === 410 || e.estado === 404)) setVencido(true);
        else setError(textoDeError(e));
      });
  }, [base]);

  useEffect(() => {
    for (const id of abiertos) {
      const clave = `${id}:${periodo}`;
      if (periodo && !docs[clave]) {
        api<Documentos>(`${base}/periodos/${periodo}/centros/${id}/documentos`)
          .then((d) => setDocs((prev) => ({ ...prev, [clave]: d })))
          .catch((e) => setError(textoDeError(e)));
      }
    }
  }, [abiertos, periodo, base, docs]);

  if (vencido) return <VencidoPage />;
  if (error)
    return (
      <div className="stage">
        <div className="card sm">
          <div className="p-body">
            <div className="ok-box bad" role="alert">
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  if (!resumen)
    return (
      <div className="stage">
        <p className="sub" style={{ color: '#fff' }}>
          {copy.comun.cargando}
        </p>
      </div>
    );

  const centrosDelPeriodo = resumen.centros.filter((ce) =>
    ce.periodos.some((p) => p.periodo === periodo),
  );
  const totalPeriodo = centrosDelPeriodo.reduce(
    (s, ce) => s + (ce.periodos.find((p) => p.periodo === periodo)?.total ?? 0),
    0,
  );
  const conmutar = (prev: Set<string>, id: string) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  };
  const alternar = (id: string) => setAbiertos((prev) => conmutar(prev, id));
  const alternarLista = (k: string) => setListas((prev) => conmutar(prev, k));

  return (
    <div className="stage">
      <section className="screen">
        <div className="card">
          <div className="p-head">
            <span className="logo-wrap">
              <img className="logo" src="/coninsa-logo.svg" alt="Coninsa" />
            </span>
            <div className="right">
              <div className="t">{c.titulo}</div>
              <div className="by">{copy.app.gobernadoPor.toUpperCase()}</div>
            </div>
          </div>
          <div className="p-body">
            <h2>{c.encabezado(nombrePeriodo(periodo))}</h2>
            <p className="sub">
              {resumen.correo} · {c.centrosAsociados(resumen.centros.length)}
            </p>

            {totalPeriodo > 0 && (
              <a className="zipall" href={`${base}/periodos/${periodo}/paquete`}>
                {c.descargarPeriodo}{' '}
                <small>{c.totalPeriodo(totalPeriodo, centrosDelPeriodo.length)}</small>
              </a>
            )}
            {centrosDelPeriodo.length === 0 && <div className="ok-box warn">{c.sinDocumentos}</div>}

            {centrosDelPeriodo.length > 0 && <div className="seccion-lbl">{c.porCentro}</div>}
            {centrosDelPeriodo.map((ce) => {
              const p = ce.periodos.find((x) => x.periodo === periodo)!;
              const abierto = abiertos.has(ce.id);
              const d = docs[`${ce.id}:${periodo}`];
              return (
                <div key={ce.id} className={`ceco ${abierto ? 'open' : ''}`}>
                  <button
                    className="ceco-h"
                    onClick={() => alternar(ce.id)}
                    aria-expanded={abierto}
                  >
                    <span className="chev">›</span>
                    <span className="badge">{ce.codigo}</span>
                    <span className="ceco-name">{ce.proyecto}</span>
                    <span className="ceco-cnt">{c.documentos(p.total)}</span>
                    <a
                      className="dl"
                      href={`${base}/periodos/${periodo}/centros/${ce.id}/paquete`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {c.descargarZip}
                    </a>
                  </button>
                  {abierto && (
                    <div className="ceco-b">
                      {!d && <div className="grp-meta">{copy.comun.cargando}</div>}
                      {d?.tipos.map((t) => {
                        const def = TIPOS_DOCUMENTO.find((x) => x.clave === t.tipo);
                        const ic = {
                          txt: def?.abrev ?? t.tipo.slice(0, 3).toUpperCase(),
                          cls: COLOR_ICONO[t.tipo] ?? 'i7',
                          meta: def?.detalle ?? 'PDF',
                        };
                        const k = `${ce.id}:${periodo}:${t.tipo}`;
                        return (
                          <div key={t.tipo}>
                            <div className="grp">
                              <div className={`ico ${ic.cls}`}>{ic.txt}</div>
                              <div className="grp-info">
                                <div className="grp-name">{t.nombre}</div>
                                <div className="grp-meta">
                                  {ic.meta} ·{' '}
                                  <button
                                    className="hist"
                                    style={{ marginRight: 0 }}
                                    onClick={() => alternarLista(k)}
                                  >
                                    {listas.has(k) ? c.ocultarLista : c.verLista}
                                  </button>
                                </div>
                              </div>
                              <div className="cnt">
                                {t.cantidad}
                                <small>{t.cantidad === 1 ? 'DOC' : 'DOCS'}</small>
                              </div>
                              <a
                                className="dl"
                                href={`${base}/periodos/${periodo}/centros/${ce.id}/tipos/${t.tipo}/paquete`}
                              >
                                {t.cantidad === 1 ? c.descargar : c.descargarZip}
                              </a>
                            </div>
                            {listas.has(k) && (
                              <ul className="docs">
                                {t.documentos.map((doc) => (
                                  <li key={doc.id}>
                                    <span>
                                      {doc.nombreArchivo}{' '}
                                      <span style={{ color: '#999' }}>
                                        {tamano(doc.tamanoBytes)}
                                      </span>
                                    </span>
                                    <a href={`${base}/documentos/${doc.id}/descarga`}>
                                      {c.descargar}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {resumen.periodos.length > 1 && (
              <div className="hist" style={{ marginTop: 18 }}>
                <b style={{ color: '#555' }}>{c.historicoTitulo}:</b>{' '}
                {resumen.periodos.map((p) => (
                  <button
                    key={p}
                    className={p === periodo ? 'on' : ''}
                    onClick={() => setPeriodo(p)}
                  >
                    {capitalizar(nombrePeriodo(p))}
                  </button>
                ))}
              </div>
            )}
            <div className="expira">
              {c.vence(
                new Date(resumen.venceEn).toLocaleDateString('es-CO', { dateStyle: 'long' }),
              )}
            </div>
          </div>
        </div>
        <div className="foot">{copy.app.pie}</div>
      </section>
    </div>
  );
}
